/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
import {
  CONTENT_TYPE_JSON,
  TOKEN_EXPIRED_MESSAGE,
} from "../../utils/apiConstants.js";
import { logger, sanitizeError } from "../../utils/logger.js";
/**
 * @fileoverview Connection logic module.
 * Handles user and company connection requests including sending,
 * accepting, rejecting, and checking connection status.
 * @module logic/connection
 */

import { refreshToken, signOut } from "../../utils/common.js";

/**
 * Sends a connection request to another user or company.
 * @async
 * @param {Object} connectionRequestData - Connection request data
 * @param {string} connectionRequestData.senderUserId - Sender user ID
 * @param {string} connectionRequestData.recipientUserId - Recipient user ID
 * @returns {Promise<Object|null>} Response data or null on error
 */
export const sendConnectionRequest = async (connectionRequestData) => {
  const options = {
    method: "POST",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify(connectionRequestData),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}connection`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        return null;
      }
      return await sendConnectionRequest(connectionRequestData);
    }
    return data;
  } catch (error) {
    logger.error("Error sending connection request:", {
      err: sanitizeError(error),
    });
    return null;
  }
};

/**
 * Checks the connection status between two companies.
 * @async
 * @param {string} senderCompanyRFC - RFC of the sender company
 * @param {string} recipientCompanyRFC - RFC of the recipient company
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Object|boolean>} Connection status data or false on error
 */
export const checkCompanyConnectionStatus = async (
  senderCompanyRFC,
  recipientCompanyRFC,
  navigate,
) => {
  const options = {
    method: "GET",
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}connection/status/${senderCompanyRFC}/${recipientCompanyRFC}`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return false;
      }
      return await checkCompanyConnectionStatus(
        senderCompanyRFC,
        recipientCompanyRFC,
        navigate,
      );
    }
    return data;
  } catch (error) {
    logger.error("Error checking company connection status:", {
      err: sanitizeError(error),
    });
    return false;
  }
};

/**
 * Fetches all pending connection requests for a user.
 * @async
 * @param {string} userId - User identifier
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Array>} Array of connection request objects
 */
export const getUserConnectionRequests = async (userId, navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}connection/user/${userId}`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await getUserConnectionRequests(userId, navigate);
    }
    return data;
  } catch (error) {
    logger.error("Error fetching user connection requests:", {
      err: sanitizeError(error),
    });
    return [];
  }
};

/**
 * Accepts a pending connection request.
 * @async
 * @param {string} requestId - Connection request identifier
 * @param {Object} requestBody - Request body with approver data
 * @param {string} requestBody.senderUserId - Original sender user ID
 * @param {string} requestBody.approverUserId - User accepting the request
 * @param {string} requestBody.approverCompanyName - Approver's company name
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Object|boolean>} Response data or false on error
 */
export const acceptConnectionRequest = async (
  requestId,
  requestBody,
  navigate,
) => {
  const options = {
    method: "POST",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify(requestBody),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}connection/${requestId}/accept`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return false;
      }
      return await acceptConnectionRequest(requestId, requestBody, navigate);
    }
    return data;
  } catch (error) {
    logger.error("Error accepting connection request:", {
      err: sanitizeError(error),
    });
    return false;
  }
};

/**
 * Rejects a pending connection request.
 * @async
 * @param {string} connectionRequestId - Connection request identifier
 * @param {Object} requestBody - Request body with rejector data
 * @param {string} requestBody.senderUserId - Original sender user ID
 * @param {string} requestBody.recipientCompanyRFC - Rejector's company RFC
 * @param {string} requestBody.recipientCompanyName - Rejector's company name
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Object|boolean>} Response data or false on error
 */
export const rejectConnectionRequest = async (
  connectionRequestId,
  requestBody,
  navigate,
) => {
  const options = {
    method: "POST",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify(requestBody),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}connection/${connectionRequestId}/reject`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return false;
      }
      return await rejectConnectionRequest(
        connectionRequestId,
        requestBody,
        navigate,
      );
    }
    return data;
  } catch (error) {
    logger.error("Error rejecting connection request:", {
      err: sanitizeError(error),
    });
    return false;
  }
};

/**
 * Updates an existing connection request with new data.
 * @async
 * @param {string} connectionRequestId - Connection request identifier
 * @param {Object} requestBody - Request body with update data
 * @param {string} [requestBody.status] - New status for the request
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Object|boolean>} Response data or false on error
 */
export const updateConnectionRequest = async (
  connectionRequestId,
  requestBody,
  navigate,
) => {
  const options = {
    method: "PATCH",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify(requestBody),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}connection/${connectionRequestId}`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return false;
      }
      return await updateConnectionRequest(
        connectionRequestId,
        requestBody,
        navigate,
      );
    }
    return data;
  } catch (error) {
    logger.error("Error updating connection request:", {
      err: sanitizeError(error),
    });
    return false;
  }
};

/**
 * Fetches connection requests between two companies.
 * @async
 * @param {string} senderCompanyRFC - RFC of the sender company
 * @param {string} recipientCompanyRFC - RFC of the recipient company
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Object>} Object with connectionRequests array and count
 */
export const getCompanyConnectionRequests = async (
  senderCompanyRFC,
  recipientCompanyRFC,
  navigate,
) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}connection/company/${senderCompanyRFC}/${recipientCompanyRFC}`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return { connectionRequests: [], count: 0 };
      }
      return await getCompanyConnectionRequests(
        senderCompanyRFC,
        recipientCompanyRFC,
        navigate,
      );
    }

    if (data?.status === "success") {
      return data;
    }

    return { connectionRequests: [], count: 0 };
  } catch (error) {
    logger.error("Error fetching company connection requests:", {
      err: sanitizeError(error),
    });
    return { connectionRequests: [], count: 0 };
  }
};
